'use client';
import { RightArrowIcon } from '@c/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import Input from '@ui/Input';
import { isSubscribedToList, subscribeToList } from '@util/firestore/messages';
import { logError } from '@util/logError';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { useState } from 'react';
import { z } from 'zod';

const emailSchema = z.string().email();

export const EnterGiveawayButton = ({ listId }: { listId: string }) => {
  const { showSuccessToast, showErrorToast } = useToastContext();
  const [email, setEmail] = useState('');
  const { userDoc } = useAuth();

  const onEnterGiveaway = async () => {
    if (!userDoc && !email) {
      showErrorToast('Please enter your email');
      return;
    }
    if (!userDoc) {
      if (!emailSchema.safeParse(email).success) {
        showErrorToast('Please enter a valid email');
        return;
      }
    }
    if (isSubscribed) {
      showErrorToast('You are already entered!');
      return;
    } else {
      try {
        setLoading(true);
        const isSubscribed = await isSubscribedToList({
          email: email ? email : userDoc?.email ?? '',
          listId: listId,
        });
        if (isSubscribed) {
          showSuccessToast('You are already entered!');
          return;
        }
        await subscribeToList(email ? email : userDoc?.email ?? '', listId);
        showSuccessToast('You are now entered in the giveaway!');
        queryClient.setQueryData([listId, userDoc?.email], true);
      } catch (error) {
        logError(
          error,
          email ? email : userDoc?.uid ?? 'giveaway-error',
          undefined,
          `Enter Giveaway Erorr - ${
            email ? email : userDoc?.email ?? 'no email provided'
          } ${(error as Error).message}`
        );
        showErrorToast('Something went wrong');
      } finally {
        setLoading(false);
      }
    }
  };
  const [loading, setLoading] = useState(false);
  const { data: isSubscribed } = useQuery({
    queryKey: [listId, userDoc?.email],
    queryFn: () =>
      isSubscribedToList({
        email: userDoc!.email,
        listId: listId,
      }),
    enabled: !!userDoc?.email,
  });

  const queryClient = useQueryClient();
  return (
    <div className="my-4 flex flex-col items-center gap-8">
      {!userDoc?.email && (
        <div className="w-full">
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      )}

      <Button
        text={isSubscribed ? 'You are entered!' : 'Enter Giveaway Now!'}
        type={isSubscribed ? 'greenBg' : 'secondary'}
        trailingIcon={
          isSubscribed ? (
            <></>
          ) : (
            <RightArrowIcon className="h-[2.5rem] w-[2.5rem]" />
          )
        }
        radius={'xl'}
        onClick={onEnterGiveaway}
        loading={loading}
      />
    </div>
  );
};
